<script setup>
import {
	Avatar,
	DmTag,
	DmDropdownMenu,
	DmIconButton,
	DmDropdownMenuItemGroup,
	Icon,
	List,
	ListBody,
	ListCell,
	ListColumn,
	ListHeader,
	ListRow,
	ListTypingFilter,
	getDateTime,
	Tooltip
} from 'dmui_components'
import ReviewAnnotations from '../../../ontology/components/ReviewAnnotations.vue'
import DeleteReport from '../delete/DeleteReport.vue'
import RetryReport from '../retry/RetryReport.vue'
import getSafeIconName from '@/utility/safeIconName'
const DEBOUNCE_TIMER = 500
const props = defineProps({
	reports: {
		type: Array,
		default: undefined
	},
	search: {
		type: Object,
		default: () => ({})
	},
	readonly: {
		type: Boolean,
		default: false
	}
})

const MIN_SEARCH_LIMIT = 2

const statusTags = {
	Processing: 'warning',
	Done: 'primary',
	Error: 'danger'
}

const emit = defineEmits(['update:search', 'delete:report', 'retry:report'])

function onUpdateSearch(key, value) {
	if (value?.length === 0 || value?.length > MIN_SEARCH_LIMIT) {
		emit('update:search', { ...props.search, [key]: value })
	}
}

function isMenuEnabled(report) {
	// This is to prevent the menu from being shown for reports that are currently processing or stuck in processing for a long time
	const isProcessing = report.status.toLowerCase() === 'processing'
	const stuckInProcessing = report.updated_at ? new Date(report.updated_at) < new Date(Date.now() - 24 * 60 * 60 * 1000) : false
	return !isProcessing || (isProcessing && stuckInProcessing)
}
</script>
<template>
	<List>
		<ListHeader>
			<ListTypingFilter :timer="DEBOUNCE_TIMER" :data="search.document_title" @update:data="onUpdateSearch('document_title', $event)"> Id </ListTypingFilter>
			<ListTypingFilter :timer="DEBOUNCE_TIMER" :data="search.org_name" @update:data="onUpdateSearch('org_name', $event)"> Company </ListTypingFilter>
			<ListColumn>Type & year</ListColumn>
			<ListColumn>Country & region</ListColumn>
			<ListColumn>Sector</ListColumn>
			<ListColumn>Status</ListColumn>
			<ListColumn>Last edited</ListColumn>
			<ListColumn><!-- Empty header for options column--></ListColumn>
		</ListHeader>
		<ListBody>
			<slot>
				<ListRow v-for="report in reports" :key="report.report_id">
					<ListCell>
						<span
							class="secondary-link single-line"
							:title="report.document_title"
							@click="$router.push(`report/edit/${report.report_id}?documentType=${report.document_type?.split(' ')[0]}`)">
							{{ report.document_title }}
						</span>
					</ListCell>

					<ListCell>
						<div class="single-line" :title="report.org_name">{{ report.org_name }}</div>
					</ListCell>
					<ListCell>
						<div class="data-container">
							<div class="secondary-text bold single-line" :title="report.document_type">{{ report.document_type }}</div>
							<div>{{ report.document_year }}</div>
						</div>
					</ListCell>
					<ListCell>
						<div class="data-container">
							<Icon :name="getSafeIconName('ic_flag', report.country_name)" size="midi" />
							<div class="multiline-data">
								<div class="single-line" :title="report.country_name">{{ report.country_name }}</div>
								<div class="single-line tiny-text" :title="report.region_name">{{ report.region_name }}</div>
							</div>
						</div>
					</ListCell>
					<ListCell>
						<DmTag v-if="report.industry_name" class="neutral overflowing-tag">
							<Icon :name="getSafeIconName('ic', report.industry_name)" size="mini" />
							<div class="single-line" :title="report.industry_name">{{ report.industry_name }}</div>
						</DmTag>
					</ListCell>
					<ListCell>
						<Tooltip position="bottom">
							<DmTag :class="statusTags[report.status]">{{ report.status }}</DmTag>
							<template #tooltip> {{ report.status_description || report.status }} </template>
						</Tooltip>
					</ListCell>
					<ListCell>
						<div class="data-container">
							<Avatar :username="report.updated_by" size="small" />
							<div class="multiline-data">
								<span class="single-line" :title="getDateTime(report.updated_at)">{{ getDateTime(report.updated_at) }}</span>
								<span class="updated-by tiny-text single-line" :title="report.updated_by">{{ report.updated_by }}</span>
							</div>
						</div>
					</ListCell>
					<ListCell>
						<DmDropdownMenu v-if="isMenuEnabled(report)">
							<template #button="{ onClick }">
								<DmIconButton id="report-options-btn" class="transparent secondary small" icon="uil-ellipsis-v" icon-size="midi" tooltip-position="top" @click="onClick">
									Options
								</DmIconButton>
							</template>
							<template #options>
								<DmDropdownMenuItemGroup>
									<ReviewAnnotations v-if="report.status.toLowerCase() === 'done'" :report="report" />
									<RetryReport
										v-if="!readonly && report.document_type !== '10k' && report.status.toLowerCase() === 'error'"
										@retry:report="$emit('retry:report', report.report_id, report.document_id, report.document_title)" />
									<DeleteReport v-if="!readonly" @delete:report="$emit('delete:report', report.document_title)" />
								</DmDropdownMenuItemGroup>
							</template>
						</DmDropdownMenu>
					</ListCell>
				</ListRow>
			</slot>
		</ListBody>
	</List>
</template>

<style scoped lang="less">
.overflowing-tag {
	max-width: 100%;
	box-sizing: border-box;
}
.data-container {
	display: flex;
	gap: @micro;
	text-transform: capitalize;
	align-items: center;
}
.multiline-data {
	display: flex;
	flex-direction: column;
	.tiny-text {
		color: @gray-80;
		line-height: 14px;
	}
}

:deep(td):not(:last-child) {
	padding-right: @midi !important;
}

:deep(td),
:deep(th) {
	max-width: 100px;
	min-width: 100px;
	&:nth-child(5),
	&:nth-child(2) {
		min-width: 190px;
		max-width: 190px;
	}

	&:nth-child(1),
	&:nth-child(4),
	&:nth-child(7) {
		min-width: 150px;
		max-width: 150px;
	}

	&:last-child {
		min-width: 40px;
		max-width: 40px;
		text-align: end;
	}
}

.single-line {
	display: block;
	max-width: 100%;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

:deep(.avatar-container) {
	min-width: @medium;
}

.updated-by {
	max-width: 120px;
}

.tooltip-container {
	cursor: pointer;
}
</style>
